import React, {useMemo} from 'react';
import {Grid} from '@mui/material';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
  BuilderDestinationColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {BuilderPlanFormsStyled} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {ContributeSourceParent} from './ContributeSourceParent';
import {ContributeDestinationParent} from './ContributeDestinationParent';
import {ContributeCashSourceRow} from './ContributeCashSourceRow';
import {ContributeBankAccountSourceRow} from './ContributeBankAccountSourceRow';
import {ContributeEmployerBankAccountSourceRow} from './ContributeEmployerBankAccountSourceRow';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {contributeFieldType, getContributeFromId} from './builderContributeFields';

import {BuilderParserStep} from '../BuilderParserStep/BuilderParserStep';
import {contributionSources, contributionDestinations} from './BuilderContributeHelpers';
import {
  changeContributeFromPlan,
  changeContributeFromBankAccount,
  changeContributeFromEmployerBankAccount,
  emptyContributeFromBase,
  getContributeDestinationFromSelection,
} from '../hooks/builderContributeHooks';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {useBuilderPlanTypes} from '../CreateBuilder/CreateBuilderHelpers';
import {UPDATES} from '../../../shared/UPDATES';
import {useAutoForm, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {useContributionPotsQuery} from './useContributionPotsQuery';

export const Contribute: React.FC<BuilderProps> = ({sourceDestinationLabels}) => {
  const {builderDetails, isSPPDA, builderReviewId} = useBuilderContext();
  const {values} = useAutoForm<builderFieldsType>();

  const {sourcePots, sourceBankAccounts, employerBankAccounts, destinationPots} =
    useContributionPotsQuery();

  const [contributeFieldObj] = useAutoField<builderFieldsType['contribute'] | undefined>(
    'contribute'
  );

  const [contributeFromObj, contributeFromMeta, contributeFromHelpers] = useAutoField<
    contributeFieldType['contributeFrom']
  >('contribute.contributeFrom');
  const {mainPlanTypes} = useBuilderPlanTypes(values);
  const [contributeToFieldObj, contributeToMeta, contributeToHelpers] = useAutoField<
    contributeFieldType['contributeTo'] | undefined
  >('contribute.contributeTo');
  const [, , destinationValuesHelpers] = useAutoField<
    contributeFieldType['destinationValues']
  >('contribute.destinationValues');

  const contributeFrom = contributeFromObj.value;
  const contributeFromId = getContributeFromId(contributeFrom);

  const sourcePotsList = useMemo(() => {
    const pots = contributionSources({
      contributeFromPots: sourcePots,
      contributeTo: contributeToFieldObj.value,
      isSppDa: isSPPDA,
    });
    return pots;
  }, [sourcePots, contributeToFieldObj.value, isSPPDA]);
  const changeSourcePlan = changeContributeFromPlan({
    contributeFromPots: sourcePots,
    contributeFromHelpers,
  });
  const changeSourceBankAccount = changeContributeFromBankAccount({
    contributeFromHelpers,
  });
  const changeSourceEmployerBankAccount = changeContributeFromEmployerBankAccount({
    contributeFromHelpers,
  });
  const destPots = useMemo(() => {
    const pots = contributionDestinations({
      destinationPots,
      contributeFieldObj,
      builderDetails,
      isSPPDA,
    });
    return pots;
  }, [destinationPots, contributeFromId, builderDetails, isSPPDA]);

  if (!values.mainPlanType) {
    return null;
  }
  if (!mainPlanTypes.length) {
    return <BuilderParserStep errorText="There are no plans to contribute from." />;
  }

  const mainPlanTypeLabel =
    mainPlanTypes.find((p) => p.value === values.mainPlanType)?.label ??
    values.mainPlanType;

  return (
    <BuilderColumnWrapper>
      <Grid item xs={6} xl={5.8} data-test="Source Choices">
        <BuilderSourceColumnHeader
          fromError={contributeFromMeta.error}
          sourceLabel={sourceDestinationLabels?.first}
        />
        <BuilderPlanFormsStyled>
          {/* SPP doesn't allow plain new cash — a contribution must be funded
              by a bank account or an existing plan. */}
          {!isSPPDA && (
            <ContributeCashSourceRow
              isSelected={contributeFrom?.sourceType === 'newCash'}
              handleChange={() =>
                contributeFromHelpers.setValue(UPDATES._D292, {
                  ...emptyContributeFromBase,
                  sourceType: 'newCash',
                })
              }
            />
          )}
          {isSPPDA &&
            sourceBankAccounts.length === 0 &&
            employerBankAccounts.length === 0 && (
              <BuilderParserStep errorText="No bank accounts to contribute from" />
            )}
          {sourceBankAccounts.map((bankAccount) => (
            <ContributeBankAccountSourceRow
              key={bankAccount.id}
              bankAccount={bankAccount}
              isSelected={
                contributeFrom?.sourceType === 'bankAccount' &&
                contributeFrom.id === bankAccount.id
              }
              handleChange={changeSourceBankAccount}
            />
          ))}
          {employerBankAccounts.map((bankAccount) => (
            <ContributeEmployerBankAccountSourceRow
              key={bankAccount.id}
              bankAccount={bankAccount}
              isSelected={
                contributeFrom?.sourceType === 'employerBankAccount' &&
                contributeFrom.id === bankAccount.id
              }
              handleChange={changeSourceEmployerBankAccount}
            />
          ))}
          {sourcePotsList.map((pot) => (
            <ContributeSourceParent
              key={pot.id}
              pot={pot}
              contributeFromId={contributeFromId}
              greyed={pot.id === contributeToFieldObj.value}
              sameReview={pot.review?.id === builderReviewId}
              handleChange={(childId) => changeSourcePlan(childId)}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
      <Grid item xs={6} xl={5.8} data-test="Destination Choices">
        <BuilderDestinationColumnHeader
          toError={contributeToMeta.error}
          destinationLabel={sourceDestinationLabels?.second}
        />
        <BuilderPlanFormsStyled>
          {destPots.length === 0 && (
            <BuilderParserStep
              errorText={`No available ${mainPlanTypeLabel} destinations`}
            />
          )}
          {destPots.map((pot) => (
            <ContributeDestinationParent
              key={pot.id}
              pot={pot}
              contributeToId={contributeToFieldObj.value}
              sameReview={pot.review?.id === builderReviewId}
              handleChange={(selectedPot) => {
                contributeToHelpers.setValue(UPDATES._D293, pot.id);
                destinationValuesHelpers.setValue(
                  UPDATES._D293,
                  getContributeDestinationFromSelection({pot: selectedPot})
                );
              }}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
    </BuilderColumnWrapper>
  );
};
